import React, { useEffect, useMemo, useState } from "react";
import EventCopyForm from "./copy-page/event-copy-form";
import { PageHeaderBar } from "../../components/page";
import { RestrictedEventVisible, RestrictedEventNotDeleted } from "pages/event/utils";
import { EventFormProvider } from "pages/event/forms/utils";
import Dashboard from "components/dashboard";
import {
  getContractHasTiiviiOtt,
  getContractService,
  getEventService,
  getEventTypesService,
  getRoomTemplatesService,
} from "../../services/commonService";
import { getDeploymentService } from "../../services/deploymentServices";
import { useQuery } from "components/graphql";
import getAssetQuery from "../asset/details-page/query";
import { useDispatch, useSelector } from "react-redux";
import { useLoadingContext } from "../../components/loading-spinner-overlay";
import { LOCAL_REC_EVENT } from "./forms/common-forms/basic-form";
import { storeTiiviiOttConfigActionCreator } from "../../actions/common-actions";
import { FormattedMessage } from "react-intl";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";
import { HTTP_ERROR_CODE_403 } from "../../utils/constants";

const useStyles = makeStyles(theme => ({
  errorTitle: {
    paddingLeft: theme.spacing(4),
  },
}));

const EventCopyPage = ({
  intl,
  history,
  match: {
    params: { id, customerId },
  },
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { setLoading } = useLoadingContext();
  const eventToCopy = useSelector(state => state.commonState.eventToCopy);

  const [event, setEvent] = useState(null);
  const [customer, setCustomer] = useState(customerId === "undefined" ? undefined : customerId);
  const [roomTemplates, setRoomTemplates] = useState(null);
  const [includeTestTemplates, setIncludeTestTemplates] = useState(true);
  const [isLocalRecEvent, setIsLocalRecEvent] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [contract, setContract] = useState(null);
  const [deployment, setDeployment] = useState(null);

  const eventServiceContractId = event?.service_contract_id;

  useEffect(
    () => {
      if (eventServiceContractId) {
        getContractService(eventServiceContractId, setContract, () => setContract(null));
      }
    },
    [eventServiceContractId],
  );

  const contractDeploymentId = contract?.deployment_id;

  useEffect(
    () => {
      if (contractDeploymentId) {
        getDeploymentService({ deploymentId: contractDeploymentId }, setDeployment, () => setDeployment(null));
      }
    },
    [contractDeploymentId],
  );

  // El REST de evento solo trae el id del asset del escudo (sport_event_data.team_logo_1/2);
  // no existe endpoint REST bajo /secure/ para resolver un asset por id, así que se usa GraphQL
  // (igual que useEventDetailsRest en el detalle/edición de evento).
  const contractCustomerId = contract?.customer_id;
  const teamLogo1Id = event?.sport_event_data?.team_logo_1;
  const teamLogo2Id = event?.sport_event_data?.team_logo_2;

  const asset1Query = useMemo(() => getAssetQuery(teamLogo1Id, contractCustomerId), [teamLogo1Id, contractCustomerId]);
  const { data: asset1Data } = useQuery(asset1Query);
  const teamLogoAsset1 = asset1Data?.asset;

  const asset2Query = useMemo(() => getAssetQuery(teamLogo2Id, contractCustomerId), [teamLogo2Id, contractCustomerId]);
  const { data: asset2Data } = useQuery(asset2Query);
  const teamLogoAsset2 = asset2Data?.asset;

  const handleSetIsLocalRecEvent = value => {
    setIsLocalRecEvent(value);
  };

  const handleSetIncludeTestTemplates = () => {
    setIncludeTestTemplates(!includeTestTemplates);
    getRoomTemplatesService(event.service_contract_id, !includeTestTemplates, r => roomTemplatesCallback(r, event));
  };

  const roomTemplatesCallback = (r, e) => {
    setRoomTemplates(r);

    const roomTemplate = r.find(roomTemplate => roomTemplate._id === e.room_template_id);

    setIsLocalRecEvent(roomTemplate?.type === LOCAL_REC_EVENT);
  };

  const tiiviiOttCallback = data => {
    dispatch(storeTiiviiOttConfigActionCreator(data.has_tiivii_ott_configured));
  };

  const errorCallback = error => {
    setLoading(false);

    if (error.status === HTTP_ERROR_CODE_403) {
      setIsAuthorized(false);
    } else {
      setErrorMessage(intl.formatMessage({ id: "event.copy.commmon.error" }));
    }
  };

  const eventCallback = r => {
    if (!customerId || customerId === "undefined") {
      const contractCallback = r => {
        setCustomer(r.customer_id);
        setLoading(false);
      };

      getContractService(r.service_contract_id, contractCallback);
    }

    const callback = response => {
      setEvent({ ...r, is_sport_event: response.is_sport_event });
      getRoomTemplatesService(r.service_contract_id, includeTestTemplates, res => roomTemplatesCallback(res, r));

      getContractHasTiiviiOtt({ contractId: r.service_contract_id }, tiiviiOttCallback);

      if (customerId !== "undefined") {
        setLoading(false);
      }
    };

    getEventTypesService(r.event_type_id, callback);
  };

  useEffect(() => {
    if (!eventToCopy || eventToCopy._id !== id) {
      setLoading(true);
      getEventService(id, eventCallback, errorCallback);
    }
  }, []);

  return (
    <Dashboard>
      <EventFormProvider>
        <RestrictedEventNotDeleted event={event}>
          <RestrictedEventVisible event={event}>
            <PageHeaderBar
              showBackButton
              title="event.copyDialog.title"
              titleParams={{ eventTitle: event?.title ?? "" }}
            />
            <Snackbar
              open={!!errorMessage}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              autoHideDuration={6000}
              onClose={() => setErrorMessage("")}
            >
              <Alert onClose={() => setErrorMessage("")} severity="error">
                {errorMessage}
              </Alert>
            </Snackbar>
            {!!event && !!customer && !!roomTemplates && (
              <EventCopyForm
                intl={intl}
                history={history}
                event={event}
                customerId={customer}
                contract={contract}
                deployment={deployment}
                roomTemplates={roomTemplates}
                includeTestTemplates={includeTestTemplates}
                handleSetIncludeTestTemplates={handleSetIncludeTestTemplates}
                isLocalRecEvent={isLocalRecEvent}
                handleSetIsLocalRecEvent={handleSetIsLocalRecEvent}
                teamLogoAsset1={teamLogoAsset1}
                teamLogoAsset2={teamLogoAsset2}
              />
            )}
            {!isAuthorized && (
              <Typography className={classes.errorTitle} variant={"h6"} gutterBottom component="h5">
                <FormattedMessage id={"event.copyDialog.403"} values={""} />
              </Typography>
            )}
          </RestrictedEventVisible>
        </RestrictedEventNotDeleted>
      </EventFormProvider>
    </Dashboard>
  );
};

export default EventCopyPage;
